<template>
  <b-input-group
    size="sm"
    :class="['counter-container row gutters-0 border rounded', { 'focused': isFocused, 'invalid': isValid === false }]"
  >
    <b-input-group-prepend
      v-if="prependIcon"
      class="col-3 prepend"
    >
      <b-input-group-text
        class="prepend flex-grow-1 bg-light p-0 py-1 d-flex align-items-center justify-content-conter"
      >
        <i :class="['fal flex-grow-1 ', prependIcon]" />
      </b-input-group-text>
    </b-input-group-prepend>
    <input
      ref="counter-input"
      type="text"
      class="counter-input form-control col-auto"
      :value="inputValue"
      :disabled="disabled"
      @input="setInputValue($event)"
      @focus="handleFocus()"
      @blur="handleBlur()"
      autocomplete="off"
    >
    <b-input-group-append
      :class="['row gutters-0 bg-light', [ unit ? 'col-5' : 'col-2' ]]"
    >
      <div
        :class="['counter-btns-container d-flex flex-column justify-content-between align-items-center', [ unit ? 'has-unit col-5' : 'col-12' ]]"
      >
        <b-button
          variant="tertiary"
          class="counter-btn"
          @click="handleIncrement()"
          :disabled="disabled || !canIncreaseValue"
        >
          <i class="fal fa-chevron-up" />
        </b-button>
        <b-button
          variant="tertiary"
          class="counter-btn"
          @click="handleDecrement()"
          :disabled="disabled || !canDecreaseValue"
        >
          <i class="fal fa-chevron-down" />
        </b-button>
      </div>
      <b-input-group-text
        v-if="unit"
        class="unit col-7 bg-light flex-grow-0 p-0 py-1 d-flex align-items-center justify-content-conter"
      >
        <p class="mb-0 d-block flex-grow-1 align-text-center">
          {{ unit }}
        </p>
      </b-input-group-text>
    </b-input-group-append>
  </b-input-group>
</template>
<script>
import numbersHelper from '@/core/mixins/numbersHelper';

export default {
  name: 'BaseCounter',
  mixins: [
    numbersHelper,
  ],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    step: {
      type: Number,
      default: 1,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    unit: {
      type: [String, null],
      required: false,
      default: null,
    },
    prependIcon: {
      type: [String, null],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      inputValue: 0,
      isFocused: false,
      isValid: null,
    };
  },
  computed: {
    canIncreaseValue() {
      return this.inputValue < Number(this.max);
    },
    canDecreaseValue() {
      return this.inputValue > Number(this.min);
    },
  },
  methods: {
    setInputValue(event) {
      const { value } = event.target;
      if (!value) {
        this.inputValue = '';
        this.isValid = null;
        return;
      }
      if (!this.isValidInteger(value) || !this.isNumberInRange(value, this.min, this.max)) {
        event.target.value = this.inputValue;
        return;
      }
      if (!this.isValid) this.isValid = this.isValidInteger(value);
      if (this.isValid) this.inputValue = this.removeLeadingZeros(value)[0];
      this.updateInput();
    },
    handleIncrement() {
      if (!this.isFocused) this.$refs['counter-input'].focus();
      if (this.inputValue < this.max) {
        let newValue = Number(this.inputValue) + this.step;
        if (newValue > this.max) newValue = this.max;
        this.inputValue = newValue;
      }
      this.updateInput();
    },
    handleDecrement() {
      if (!this.isFocused) this.$refs['counter-input'].focus();
      if (this.inputValue > this.min) {
        let newValue = Number(this.inputValue) - this.step;
        if (newValue < this.min) newValue = this.min;
        this.inputValue = newValue;
      }
      this.updateInput();
    },
    updateInput() {
      this.$emit('updateValue', this.inputValue);
    },
    handleFocus() {
      this.isFocused = true;
      this.isValid = null;
      this.$emit('focus');
      window.addEventListener('keydown', this.handleKeydown);
    },
    handleBlur() {
      this.isFocused = false;
      if (!this.isValidInteger(this.inputValue)) this.isValid = false;
      this.$emit('blur');
      window.removeEventListener('keydown', this.handleKeydown);
    },
    handleKeydown(event) {
      if (event.key === 'ArrowUp' && this.inputValue < Number(this.max)) this.inputValue++;
      else if (event.key === 'ArrowDown' && this.inputValue > Number(this.min)) this.inputValue--;
      else if (event.key === 'Enter') this.$refs['counter-input'].blur();
      else if (event.key === 'Escape') {
        this.inputValue = this.value;
        this.$refs['counter-input'].blur();
      }
    },
  },
  mounted() {
    let valueLocal = this.value;
    if (typeof this.value === 'string') {
      valueLocal = valueLocal.replace(this.unit, '');
    }
    valueLocal = Number(valueLocal);
    if (valueLocal < this.min) this.inputValue = this.min;
    else if (valueLocal > this.max) this.inputValue = this.max;
    else this.inputValue = valueLocal;
  },
};
</script>
<style lang="scss" scoped>
.counter-container {
  width: 100%;
  overflow: hidden;
  &.focused {
    border-color: $link !important;
    outline: .2rem solid rgba(0, 123, 255, 0.25);
  }
  &.invalid {
    outline: none;
    border-color: $danger !important;
  }
}
.counter-input {
  text-align: center;
  border: unset;
  &:focus {
    outline: none;
    -webkit-box-shadow: none;
    box-shadow: none;
  }
}
.counter-btns-container {
  border-left: 1px solid $gray-300;
  &.has-unit {
    border-right: 1px solid $gray-300;
  }
  padding: 2px 0;
}
.counter-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  border: unset;
  border-radius: unset;
  min-width: unset;
  width: 100%;
  padding: 0 !important;
  margin: 0 !important;
  line-height: unset;
  font-size: 75%;
  &:hover {
    border: unset;
  }
}
.unit {
  line-height: unset;
  border: unset;
  p {
    margin: 0;
    padding: 0;
  }
}
.prepend {
  line-height: unset;
  border: unset;
  border-right: 1px solid $gray-300 !important;
  i {
    margin: 0;
    padding: 0;
  }
}
</style>
